import { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { 
  Plane, Briefcase, Heart, Map, Compass, 
  Clock, Users, Car, CreditCard, Globe,
  Phone, Mail, MapPin, ChevronDown, Menu, X,
  Star, CheckCircle, Shield, ArrowRight
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { format } from "date-fns";
import { it, enUS } from "date-fns/locale";
import SEO from "../components/SEO";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { trackEvent } from "../lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "+393283796150";
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER.replace(/[^0-9]/g, '')}`;

// Images
const IMAGES = {
  hero: "https://images.unsplash.com/photo-1564181064972-432b6f96c0ef?w=1920&q=80",
  chauffeur: "https://customer-assets.emergentagent.com/job_09b7cb9d-9c9c-4ed4-b151-2318db7aea34/artifacts/jh3lyqae_business-plan-per-unazienda-di-NCC.jpg",
  mercedes: "https://customer-assets.emergentagent.com/job_09b7cb9d-9c9c-4ed4-b151-2318db7aea34/artifacts/9clppwhp_Business_Class_Ncc_LVF8682_1-1024x683.jpg",
  interior: "https://images.unsplash.com/photo-1662580424856-e8000e2ef130?w=800&q=80",
  airport: "https://images.squarespace-cdn.com/content/v1/690f381fe8f7b37dcf97958b/b72b31f6-5a0f-49af-b8ff-83591c95fc1c/ChatGPT+Image+19+nov+2025%2C+16_55_51.png",
  logo: "/black-arrow-logo.png",
  cla250: "https://customer-assets.emergentagent.com/job_ca31ceaa-5be0-4dce-a1ee-0adc325d4aa0/artifacts/1cnj4g5e_Screenshot%202026-04-21%20213827.png",
  tourneo: "https://customer-assets.emergentagent.com/job_ca31ceaa-5be0-4dce-a1ee-0adc325d4aa0/artifacts/rhamvub1_Screenshot%202026-04-21%20213345.png"
};

// Navigation Component
const Navigation = () => {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMobileMenuOpen(false);
  };

  return (
    <nav 
      data-testid="navigation"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled ? "glassmorphism py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="container-custom flex items-center justify-between">
        <a href="/" className="flex items-center gap-3" data-testid="logo-link">
          <img src={IMAGES.logo} alt="Black Arrow NCC" className="h-16 w-auto object-contain" />
          <span className="font-serif text-xl text-white tracking-wide">Black Arrow NCC</span>
        </a>

        {/* Desktop Menu */}
        <div className="hidden lg:flex items-center gap-8">
          <button onClick={() => scrollToSection("chi-siamo")} className="nav-link text-sm uppercase tracking-widest font-medium" data-testid="nav-about">
            {t("nav.about")}
          </button>
          <button onClick={() => scrollToSection("servizi")} className="nav-link text-sm uppercase tracking-widest font-medium" data-testid="nav-services">
            {t("nav.services")}
          </button>
          <button onClick={() => scrollToSection("flotta")} className="nav-link text-sm uppercase tracking-widest font-medium" data-testid="nav-fleet">
            {t("nav.fleet")}
          </button>
          <button onClick={() => scrollToSection("perche-noi")} className="nav-link text-sm uppercase tracking-widest font-medium" data-testid="nav-why-us">
            {t("nav.why")}
          </button>
          <button onClick={() => scrollToSection("prenota")} className="nav-link text-sm uppercase tracking-widest font-medium" data-testid="nav-booking">
            {t("nav.book")}
          </button>
          <a 
            href="/transfer-aeroporto-napoli" 
            className="text-gold text-sm uppercase tracking-widest font-medium hover:text-gold-light transition-colors"
            data-testid="nav-naples"
          >
            {t("nav.airport")}
          </a>
        </div>

        {/* CTA Buttons */}
        <div className="hidden lg:flex items-center gap-4">
          <LanguageSwitcher />
          <a href={`tel:${WHATSAPP_NUMBER}`} onClick={() => trackEvent("phone_click", { location: "header" })} className="text-white hover:text-gold transition-colors" data-testid="header-phone">
            <Phone className="h-5 w-5" />
          </a>
          <Button 
            onClick={() => { trackEvent("cta_click", { cta: "prenota_ora", location: "header" }); scrollToSection("prenota"); }}
            className="btn-gold px-6 py-2 font-medium"
            data-testid="header-book-btn"
          >
            {t("nav.book_now")}
          </Button>
        </div>

        {/* Mobile Menu Button */}
        <div className="lg:hidden flex items-center gap-3">
          <LanguageSwitcher variant="mobile" />
          <button 
            className="text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden absolute top-full left-0 w-full bg-black/95 backdrop-blur-lg border-t border-white/10 py-6" data-testid="mobile-menu">
          <div className="container-custom flex flex-col gap-4">
            <button onClick={() => scrollToSection("chi-siamo")} className="text-left text-white py-2 uppercase tracking-widest text-sm">{t("nav.about")}</button>
            <button onClick={() => scrollToSection("servizi")} className="text-left text-white py-2 uppercase tracking-widest text-sm">{t("nav.services")}</button>
            <button onClick={() => scrollToSection("flotta")} className="text-left text-white py-2 uppercase tracking-widest text-sm">{t("nav.fleet")}</button>
            <button onClick={() => scrollToSection("perche-noi")} className="text-left text-white py-2 uppercase tracking-widest text-sm">{t("nav.why")}</button>
            <button onClick={() => scrollToSection("prenota")} className="text-left text-white py-2 uppercase tracking-widest text-sm">{t("nav.book")}</button>
            <a href="/transfer-aeroporto-napoli" className="text-gold py-2 uppercase tracking-widest text-sm">{t("nav.airport")}</a>
            <Button onClick={() => scrollToSection("prenota")} className="btn-gold w-full mt-4">{t("nav.book_now")}</Button>
          </div>
        </div>
      )}
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  const { t } = useTranslation();
  const scrollToBooking = () => {
    document.getElementById("prenota")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="hero-section" data-testid="hero-section">
      <div className="hero-bg" style={{ backgroundImage: `url(${IMAGES.hero})` }} />
      <div className="absolute inset-0 hero-overlay" />
      
      <div className="hero-content max-w-4xl mx-auto">
        <p className="text-gold uppercase tracking-[0.3em] text-sm md:text-base mb-6 animate-fade-in-up opacity-0" style={{ animationDelay: '0.2s' }}>
          {t("hero.subtitle").split(".")[0]}
        </p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl text-white mb-6 leading-tight animate-fade-in-up opacity-0" style={{ animationDelay: '0.4s' }}>
          <span className="gold-text">{t("hero.title")}</span>
        </h1>
        <p className="text-muted-foreground text-lg md:text-xl mb-10 max-w-2xl mx-auto animate-fade-in-up opacity-0" style={{ animationDelay: '0.6s' }}>
          {t("hero.subtitle")}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up opacity-0" style={{ animationDelay: '0.8s' }}>
          <Button 
            onClick={() => { trackEvent("cta_click", { cta: "prenota_ora", location: "hero" }); scrollToBooking(); }}
            className="btn-gold px-10 py-6 text-lg font-medium"
            data-testid="hero-book-btn"
          >
            {t("hero.cta_book")}
          </Button>
          <a 
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent("whatsapp_click", { location: "hero" })}
            className="btn-outline-gold px-10 py-6 text-lg font-medium inline-flex items-center justify-center gap-2"
            data-testid="hero-whatsapp-btn"
          >
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            {t("hero.cta_whatsapp")}
          </a>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <ChevronDown className="h-8 w-8 text-gold/50" />
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const { t } = useTranslation();
  const list = t("about.list", { returnObjects: true });
  return (
    <section id="chi-siamo" className="section bg-luxe-dark relative" data-testid="about-section">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-gold uppercase tracking-[0.2em] text-sm mb-4">{t("about.eyebrow")}</p>
            <h2 className="font-serif text-3xl md:text-5xl text-white mb-8">
              {t("about.title")}
            </h2>
            <div className="space-y-5 text-muted-foreground leading-relaxed">
              <p>
                <span className="text-gold font-semibold">{t("about.p1_prefix")}</span> {t("about.p1")}
              </p>
              <p>
                {t("about.p2_prefix")} <span className="text-gold">{t("about.p2_gold")}</span>{t("about.p2_suffix")}
              </p>
              <p>{t("about.p3")}</p>
              <div className="pt-2">
                <p className="text-white font-medium mb-3">{t("about.list_title")}</p>
                <ul className="space-y-2">
                  {list.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-gold shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <p className="text-gold font-serif italic">
                {t("about.quote")}
              </p>
            </div>
            <div className="flex flex-wrap gap-8 mt-10">
              <div>
                <p className="font-serif text-4xl text-gold">500+</p>
                <p className="text-muted-foreground text-sm uppercase tracking-wide">{t("about.stat_clients")}</p>
              </div>
              <div>
                <p className="font-serif text-4xl text-gold">24/7</p>
                <p className="text-muted-foreground text-sm uppercase tracking-wide">{t("about.stat_availability")}</p>
              </div>
              <div>
                <p className="font-serif text-4xl text-gold">100%</p>
                <p className="text-muted-foreground text-sm uppercase tracking-wide">{t("about.stat_authorized")}</p>
              </div>
            </div>
          </div>
          <div className="relative">
            <img 
              src={IMAGES.chauffeur}
              alt="Chauffeur"
              className="w-full h-[500px] object-cover"
            />
            <div className="absolute -bottom-6 -left-6 bg-gold p-6">
              <p className="font-serif text-2xl text-black">{t("about.badge_title")}</p>
              <p className="text-black/70 text-sm">{t("about.badge_since")}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServiceIcon = ({ name }) => {
  const icons = { Plane, Briefcase, Heart, Map, Compass };
  const Icon = icons[name] || Plane;
  return <Icon className="h-8 w-8 text-gold" />;
};

const ServicesSection = () => {
  const { t } = useTranslation();
  const services = [
    { icon: "Plane", key: "airport" },
    { icon: "Briefcase", key: "business" },
    { icon: "Heart", key: "events" },
    { icon: "Map", key: "long" },
    { icon: "Compass", key: "tourism" }
  ];

  return (
    <section id="servizi" className="section bg-luxe-charcoal" data-testid="services-section">
      <div className="container-custom">
        <div className="text-center mb-16">
          <p className="text-gold uppercase tracking-[0.2em] text-sm mb-4">{t("services.eyebrow")}</p>
          <h2 className="font-serif text-3xl md:text-5xl text-white">
            {t("services.title")}
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service) => (
            <div 
              key={service.key}
              className="service-card p-8 group cursor-pointer"
              data-testid={`service-card-${service.key}`}
            >
              <div className="mb-6">
                <ServiceIcon name={service.icon} />
              </div>
              <h3 className="font-serif text-xl text-white mb-4 group-hover:text-gold transition-colors">
                {t(`services.items.${service.key}.title`)}
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {t(`services.items.${service.key}.desc`)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Fleet Section
const FleetSection = () => {
  const { t } = useTranslation();
  const vehicles = [
    {
      id: "tourneo",
      image: IMAGES.tourneo,
    },
    {
      id: "cla250",
      image: IMAGES.cla250,
    }
  ];

  return (
    <section id="flotta" className="section bg-luxe-spotlight-right" data-testid="fleet-section">
      <div className="container-custom">
        <div className="text-center mb-16">
          <p className="text-gold uppercase tracking-[0.2em] text-sm mb-4">{t("fleet.eyebrow")}</p>
          <h2 className="font-serif text-3xl md:text-5xl text-white mb-6">
            {t("fleet.title_l1")}<br />{t("fleet.title_l2")}
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {t("fleet.desc")}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8" data-testid="fleet-grid">
          {vehicles.map((v) => {
            const features = t(`fleet.${v.id}.features`, { returnObjects: true });
            return (
              <div key={v.id} className="bg-card border border-border group hover:border-gold/50 transition-all duration-500 overflow-hidden" data-testid={`fleet-${v.id}`}>
                <div className="relative overflow-hidden h-72 bg-black">
                  <img
                    src={v.image}
                    alt={t(`fleet.${v.id}.name`)}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute top-4 left-4 bg-gold text-black px-4 py-1 text-xs uppercase tracking-widest font-medium">
                    {t(`fleet.${v.id}.category`)}
                  </div>
                </div>

                <div className="p-8">
                  <h3 className="font-serif text-2xl text-white mb-2 group-hover:text-gold transition-colors">
                    {t(`fleet.${v.id}.name`)}
                  </h3>
                  <p className="text-muted-foreground text-sm italic mb-6">{t(`fleet.${v.id}.ideal`)}</p>

                  <div className="grid grid-cols-2 gap-4 mb-6 pb-6 border-b border-border">
                    <div className="flex items-start gap-2">
                      <Users className="h-4 w-4 text-gold mt-1 shrink-0" />
                      <div>
                        <p className="text-white/50 text-xs uppercase tracking-wider">{t("fleet.capacity")}</p>
                        <p className="text-white text-sm">{t(`fleet.${v.id}.capacity`)}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <Briefcase className="h-4 w-4 text-gold mt-1 shrink-0" />
                      <div>
                        <p className="text-white/50 text-xs uppercase tracking-wider">{t("fleet.luggage")}</p>
                        <p className="text-white text-sm">{t(`fleet.${v.id}.luggage`)}</p>
                      </div>
                    </div>
                  </div>

                  <ul className="space-y-2">
                    {features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm text-white/80">
                        <CheckCircle className="h-4 w-4 text-gold shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-12 flex flex-wrap gap-4 justify-center">
          {["drivers", "insured", "tailored"].map((k) => (
            <div key={k} className="bg-secondary p-4 border border-border text-center min-w-[200px]">
              <p className="text-gold font-serif text-lg">{t(`fleet.footer_cards.${k}.title`)}</p>
              <p className="text-muted-foreground text-sm">{t(`fleet.footer_cards.${k}.desc`)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyChooseUsSection = () => {
  const { t } = useTranslation();
  const reasons = [
    { icon: Clock, key: "24h" },
    { icon: Users, key: "drivers" },
    { icon: CreditCard, key: "prices" },
    { icon: Shield, key: "licensed" }
  ];

  return (
    <section id="perche-noi" className="section bg-luxe-charcoal" data-testid="why-us-section">
      <div className="container-custom">
        <div className="text-center mb-16">
          <p className="text-gold uppercase tracking-[0.2em] text-sm mb-4">{t("why.eyebrow")}</p>
          <h2 className="font-serif text-3xl md:text-5xl text-white">
            {t("why.title")}
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {reasons.map((reason) => (
            <div key={reason.key} className="text-center p-6" data-testid={`reason-${reason.key}`}>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-secondary border border-border mb-6">
                <reason.icon className="h-8 w-8 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-white mb-3">{t(`why.items.${reason.key}.title`)}</h3>
              <p className="text-muted-foreground text-sm">{t(`why.items.${reason.key}.desc`)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const { t } = useTranslation();
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API}/testimonials`);
        setTestimonials(response.data.testimonials);
      } catch (err) {
        if (process.env.NODE_ENV === "development") {
          console.error("Failed to fetch testimonials:", err);
        }
      }
    };
    fetchTestimonials();
  }, []);

  return (
    <section className="section bg-luxe-cream" data-testid="testimonials-section">
      <div className="container-custom">
        <div className="text-center mb-16">
          <p className="text-gold uppercase tracking-[0.2em] text-sm mb-4">{t("testimonials.eyebrow")}</p>
          <h2 className="font-serif text-3xl md:text-5xl text-white">
            {t("testimonials.title")}
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {testimonials.map((testimonial) => (
            <div key={testimonial.id} className="testimonial-card" data-testid={`testimonial-${testimonial.id}`}>
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-gold text-gold" />
                ))}
              </div>
              <p className="text-white text-lg mb-6 italic">&ldquo;{testimonial.text}&rdquo;</p>
              <div>
                <p className="text-gold font-medium">{testimonial.name}</p>
                <p className="text-muted-foreground text-sm">{testimonial.location}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Booking Section
const BookingSection = () => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "en" ? enUS : it;
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    pickup_location: "",
    destination: "",
    time: "",
    notes: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!date) {
      toast.error(t("booking.form.toast_no_date"));
      return;
    }

    setLoading(true);
    trackEvent("booking_submit_attempt", { page: "home" });
    try {
      await axios.post(`${API}/bookings`, {
        ...formData,
        date: format(date, "yyyy-MM-dd")
      });
      trackEvent("booking_submit_success", { page: "home" });
      toast.success(t("booking.form.toast_success"));
      setFormData({ name: "", phone: "", email: "", pickup_location: "", destination: "", time: "", notes: "" });
      setDate(null);
    } catch (error) {
      trackEvent("booking_submit_error", { page: "home" });
      toast.error(t("booking.form.toast_error"));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="prenota" className="section bg-luxe-spotlight-right" data-testid="booking-section">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <p className="text-gold uppercase tracking-[0.2em] text-sm mb-4">{t("booking.eyebrow")}</p>
            <h2 className="font-serif text-3xl md:text-5xl text-white mb-8">
              {t("booking.title_l1")} {t("booking.title_l2")}
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-10">
              {t("booking.desc")}
            </p>

            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-secondary border border-border flex items-center justify-center">
                  <Phone className="h-5 w-5 text-gold" />
                </div>
                <div>
                  <p className="text-muted-foreground text-sm">{t("booking.call")}</p>
                  <a href={`tel:${WHATSAPP_NUMBER}`} className="text-white hover:text-gold transition-colors" data-testid="contact-phone">
                    +39 328 379 6150
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-secondary border border-border flex items-center justify-center">
                  <Mail className="h-5 w-5 text-gold" />
                </div>
                <div>
                  <p className="text-muted-foreground text-sm">{t("booking.write")}</p>
                  <a href="mailto:blackarrow25@libero.it" onClick={() => trackEvent("email_click", { location: "contact_section" })} className="text-white hover:text-gold transition-colors" data-testid="contact-email">
                    blackarrow25@libero.it
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-secondary border border-border flex items-center justify-center">
                  <MapPin className="h-5 w-5 text-gold" />
                </div>
                <div>
                  <p className="text-muted-foreground text-sm">{t("booking.address")}</p>
                  <p className="text-white">Viale Libertà 55, Paduli (BN), Italia</p>
                </div>
              </div>
            </div>

            <div className="mt-10">
              <a 
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("whatsapp_click", { location: "contact_section" })}
                className="inline-flex items-center gap-3 bg-[#25D366] text-white px-8 py-4 font-medium hover:bg-[#1da851] transition-colors"
                data-testid="whatsapp-cta"
              >
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                {t("booking.chat_whatsapp")}
              </a>
            </div>
          </div>

          <div className="bg-card border border-border p-8 md:p-10">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="booking-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">{t("booking.form.name")} *</label>
                  <Input name="name" value={formData.name} onChange={handleChange} required className="form-input w-full" placeholder={t("booking.form.name_ph")} data-testid="input-name" />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">{t("booking.form.phone")} *</label>
                  <Input name="phone" value={formData.phone} onChange={handleChange} required className="form-input w-full" placeholder={t("booking.form.phone_ph")} data-testid="input-phone" />
                </div>
              </div>

              <div>
                <label className="text-sm text-muted-foreground mb-2 block">{t("booking.form.email")} *</label>
                <Input name="email" type="email" value={formData.email} onChange={handleChange} required className="form-input w-full" placeholder={t("booking.form.email_ph")} data-testid="input-email" />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">{t("booking.form.pickup")} *</label>
                  <Input name="pickup_location" value={formData.pickup_location} onChange={handleChange} required className="form-input w-full" placeholder={t("booking.form.pickup_ph")} data-testid="input-pickup" />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">{t("booking.form.destination")} *</label>
                  <Input name="destination" value={formData.destination} onChange={handleChange} required className="form-input w-full" placeholder={t("booking.form.destination_ph")} data-testid="input-destination" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">{t("booking.form.date")} *</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="form-input w-full justify-start text-left font-normal" data-testid="date-picker">
                        {date ? format(date, "d MMMM yyyy", { locale: dateLocale }) : <span className="text-muted-foreground">{t("booking.form.date_ph")}</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-card border-border" align="start">
                      <Calendar mode="single" selected={date} onSelect={setDate} disabled={(date) => date < new Date()} locale={dateLocale} initialFocus />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">{t("booking.form.time")} *</label>
                  <Input name="time" type="time" value={formData.time} onChange={handleChange} required className="form-input w-full" data-testid="input-time" />
                </div>
              </div>

              <div>
                <label className="text-sm text-muted-foreground mb-2 block">{t("booking.form.notes")}</label>
                <Textarea name="notes" value={formData.notes} onChange={handleChange} className="form-input w-full min-h-[100px]" placeholder={t("booking.form.notes_ph")} data-testid="input-notes" />
              </div>

              <Button type="submit" disabled={loading} className="btn-gold w-full py-6 text-lg font-medium" data-testid="submit-booking">
                {loading ? (
                  <span className="flex items-center gap-2"><span className="spinner" />{t("booking.form.submitting")}</span>
                ) : (
                  <>{t("booking.form.submit")} <ArrowRight className="ml-2 h-5 w-5" /></>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const { t } = useTranslation();
  const links = [
    { key: "about", id: "chi-siamo" },
    { key: "services", id: "servizi" },
    { key: "fleet", id: "flotta" },
    { key: "book", id: "prenota" },
  ];
  return (
    <footer className="bg-luxe-dark border-t border-gold/20 py-16" data-testid="footer">
      <div className="container-custom">
        <div className="grid md:grid-cols-4 gap-12">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <img src={IMAGES.logo} alt="Black Arrow NCC" className="h-12 w-12 object-contain" />
              <span className="font-serif text-2xl text-white">Black Arrow NCC</span>
            </div>
            <p className="text-muted-foreground leading-relaxed max-w-md">
              {t("footer.tagline")}
            </p>
          </div>

          <div>
            <h4 className="font-serif text-lg text-gold mb-6">{t("footer.quick_links")}</h4>
            <ul className="space-y-3">
              {links.map((link) => (
                <li key={link.id}>
                  <button onClick={() => document.getElementById(link.id)?.scrollIntoView({ behavior: "smooth" })} className="text-muted-foreground hover:text-gold transition-colors">
                    {t(`nav.${link.key}`)}
                  </button>
                </li>
              ))}
              <li><a href="/transfer-aeroporto-napoli" className="text-muted-foreground hover:text-gold transition-colors">{t("nav.airport")}</a></li>
            </ul>
          </div>

          <div>
            <h4 className="font-serif text-lg text-gold mb-6">{t("footer.contacts")}</h4>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-gold" />
                <a href={`tel:${WHATSAPP_NUMBER}`} className="hover:text-gold transition-colors">+39 328 379 6150</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-gold" />
                <a href="mailto:blackarrow25@libero.it" className="hover:text-gold transition-colors">blackarrow25@libero.it</a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-gold mt-1" />
                <span>Viale Libertà 55, Paduli (BN)</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="section-divider my-10" />

        {/* PNRR / EU Funding Disclosure */}
        <div className="mb-8 flex justify-end" data-testid="pnrr-badge">
          <div className="bg-white py-2 px-3 inline-flex">
            <img
              src="/pnrr-eu-mic.png"
              alt="Finanziato dall'Unione europea NextGenerationEU — Ministero della Cultura"
              className="h-12 md:h-14 w-auto object-contain"
            />
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-muted-foreground text-sm">
            © {new Date().getFullYear()} Black Arrow NCC. {t("footer.rights")}
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground justify-center md:justify-end">
            <button type="button" onClick={() => window.dispatchEvent(new Event("open-privacy"))} className="hover:text-gold transition-colors" data-testid="footer-privacy-link">{t("footer.privacy")}</button>
            <button type="button" onClick={() => window.dispatchEvent(new Event("open-cookie-policy"))} className="hover:text-gold transition-colors" data-testid="footer-cookie-link">{t("footer.cookie")}</button>
            <button type="button" onClick={() => window.dispatchEvent(new Event("open-cookie-preferences"))} className="hover:text-gold transition-colors" data-testid="footer-cookie-prefs-link">{t("footer.manage_cookies")}</button>
          </div>
        </div>
      </div>
    </footer>
  );
};

// WhatsApp Floating Button
const WhatsAppFloat = () => {
  return (
    <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("whatsapp_click", { location: "floating_button" })} className="whatsapp-float" data-testid="whatsapp-float" aria-label="Chatta su WhatsApp">
      <svg className="h-7 w-7 text-white" fill="currentColor" viewBox="0 0 24 24">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
    </a>
  );
};

// Main HomePage Component
const HomePage = () => {
  const { i18n } = useTranslation();
  const isEn = i18n.language === "en";
  return (
    <div className="scroll-smooth">
      <SEO
        title={isEn
          ? "Black Arrow NCC | Naples Chauffeur Service, Capodichino Airport Transfer"
          : "Black Arrow NCC | Noleggio Con Conducente Napoli, Transfer Aeroporto Capodichino"}
        description={isEn
          ? "Premium chauffeur service in Naples and throughout Italy. Capodichino airport transfers, business trips, Amalfi Coast tours. Mercedes CLA and Ford Tourneo 9-seat fleet, professional drivers 24/7."
          : "Servizio NCC a Napoli e in tutta Italia. Transfer aeroporto Capodichino, viaggi business, Costiera Amalfitana. Flotta Mercedes CLA e Ford Tourneo 9 posti, autisti professionali 24/7."}
        canonical="https://blackarrowncc.it/"
      />
      <Navigation />
      <HeroSection />
      <AboutSection />
      <ServicesSection />
      <FleetSection />
      <WhyChooseUsSection />
      <TestimonialsSection />
      <BookingSection />
      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

export default HomePage;
